/**
 * SoleadLab 2.0 - Thermal Annotator Hook
 *
 * React hook that manages the thermal annotation state machine:
 * ROI rectangles, point measurements, drag interactions, and
 * client-side DJI R-JPEG parsing.
 *
 * Ports the original ThermalAnnotatorModule class from editor (23).html
 * lines 7290-7616 into idiomatic React with hooks.
 *
 * KEY DIFFERENCE from original: temperature computations happen locally
 * on the in-memory Float32Array (no API calls). Results are instant.
 */

import { useState, useRef, useCallback } from 'react';
import {
  parseDjiThermal,
  recalcTemperatures,
  getRoiStatsDisplay,
  getPointTempDisplay,
} from '../../../utils/djiThermalParser';
import type { ThermalImage } from '../../../utils/djiThermalParser';
import type {
  ThermalRoi,
  ThermalPoint,
  RadiometricParams,
  ThermalAnalysisData,
  AnnotatorMode,
  DragState,
} from './thermalTypes';
import { DEFAULT_RADIOMETRIC_PARAMS } from './thermalTypes';

// ---------------------------------------------------------------------------
// Hit-test constants (in image-pixel space)
// ---------------------------------------------------------------------------
const HANDLE_RADIUS = 8;
const POINT_HIT_RADIUS = 12;

/** Status-line dimensions — flags super-res photos where JPEG ≠ raw matrix */
function formatDims(ti: ThermalImage): string {
  const mismatch = ti.displayWidth && ti.displayWidth !== ti.width;
  return mismatch
    ? `${ti.width}x${ti.height} (JPEG ${ti.displayWidth}x${ti.displayHeight})`
    : `${ti.width}x${ti.height}`;
}

// ---------------------------------------------------------------------------
// Hook interface
// ---------------------------------------------------------------------------

export interface UseThermalAnnotatorReturn {
  // State
  mode: AnnotatorMode;
  rois: ThermalRoi[];
  points: ThermalPoint[];
  activeRoiIndex: number;
  activePointIndex: number;
  isAnalyzing: boolean;
  isAnalyzed: boolean;
  statusMessage: string;
  params: RadiometricParams;

  // Mode
  setMode: (mode: AnnotatorMode) => void;

  // Analysis
  analyze: (file: File) => Promise<void>;
  setParams: (p: RadiometricParams) => void;

  // Actions
  undoRoi: () => void;
  clearRois: () => void;
  clearPoints: () => void;

  // Canvas event handlers (coordinates in image-pixel space)
  handlePointerDown: (imgX: number, imgY: number) => void;
  handlePointerMove: (imgX: number, imgY: number) => void;
  handlePointerUp: () => void;

  // Serialization
  toJSON: () => ThermalAnalysisData;
  load: (data: ThermalAnalysisData | undefined) => void;

  // Snapshot
  exportSnapshotDataURL: (img: HTMLImageElement, drawFn: (ctx: CanvasRenderingContext2D) => void) => string | null;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useThermalAnnotator(
  onChange?: (data: ThermalAnalysisData) => void,
): UseThermalAnnotatorReturn {
  const [mode, setModeState] = useState<AnnotatorMode>('rect');
  const [rois, setRois] = useState<ThermalRoi[]>([]);
  const [points, setPoints] = useState<ThermalPoint[]>([]);
  const [activeRoiIndex, setActiveRoiIndex] = useState(-1);
  const [activePointIndex, setActivePointIndex] = useState(-1);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [params, setParamsState] = useState<RadiometricParams>({ ...DEFAULT_RADIOMETRIC_PARAMS });

  // Mutable refs for drag state (avoid re-renders during drag)
  const dragRef = useRef<DragState>(null);
  const thermalImageRef = useRef<ThermalImage | null>(null);

  // Track analyzed state
  const isAnalyzed = thermalImageRef.current !== null;

  // ---- Notify parent of changes ----
  const notifyChange = useCallback((
    newRois: ThermalRoi[],
    newPoints: ThermalPoint[],
    newParams: RadiometricParams,
  ) => {
    onChange?.({
      rois: newRois,
      points: newPoints,
      params: newParams,
    });
  }, [onChange]);

  // ---- Set mode ----
  const setMode = useCallback((m: AnnotatorMode) => {
    setModeState(m);
    setActiveRoiIndex(-1);
    setActivePointIndex(-1);
  }, []);

  // ---- Normalize ROI (ensure x0 < x1, y0 < y1) ----
  const normRoi = (r: ThermalRoi): ThermalRoi => ({
    ...r,
    x0: Math.min(r.x0, r.x1),
    y0: Math.min(r.y0, r.y1),
    x1: Math.max(r.x0, r.x1),
    y1: Math.max(r.y0, r.y1),
  });

  // ---- Compute ROI stats from local temperature matrix ----
  const computeRoiStats = useCallback((roi: ThermalRoi): ThermalRoi => {
    const ti = thermalImageRef.current;
    if (!ti) return roi;
    const nr = normRoi(roi);
    const stats = getRoiStatsDisplay(ti, nr.x0, nr.y0, nr.x1, nr.y1);
    return { ...roi, stats };
  }, []);

  // ---- Compute point temp from local temperature matrix ----
  const computePointTemp = useCallback((pt: ThermalPoint): ThermalPoint => {
    const ti = thermalImageRef.current;
    if (!ti) return pt;
    const value_c = getPointTempDisplay(ti, pt.x, pt.y);
    return { ...pt, value_c };
  }, []);

  // ---- Analyze ----
  const analyze = useCallback(async (file: File) => {
    setIsAnalyzing(true);
    setStatusMessage('Analyzing...');
    try {
      const buffer = await file.arrayBuffer();
      const ti = await parseDjiThermal(buffer, params);
      thermalImageRef.current = ti;
      setStatusMessage(`${formatDims(ti)} · ${ti.cameraModel} · ${ti.minTemp.toFixed(1)}C to ${ti.maxTemp.toFixed(1)}C`);
      // Re-compute any existing ROIs/points with new thermal data. Both lists
      // MUST be read through functional updates (same pattern as
      // handlePointerUp): callers may invoke a stale reference to `analyze`
      // (e.g. the Classify panel's mount-pinned debounced auto-analyze), so
      // closure state here can predate `load()` — a closure read of `points`
      // used to notify `points: []` and silently wipe restored annotations.
      setRois((prevRois) => {
        const updatedRois = prevRois.map((r) => {
          const nr = normRoi(r);
          const stats = getRoiStatsDisplay(ti, nr.x0, nr.y0, nr.x1, nr.y1);
          return { ...r, stats };
        });
        setPoints((prevPoints) => {
          const updatedPoints = prevPoints.map((p) => {
            const value_c = getPointTempDisplay(ti, p.x, p.y);
            return { ...p, value_c };
          });
          notifyChange(updatedRois, updatedPoints, params);
          return updatedPoints;
        });
        return updatedRois;
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Analysis failed';
      setStatusMessage(msg);
      thermalImageRef.current = null;
    } finally {
      setIsAnalyzing(false);
    }
  }, [params, notifyChange]);

  // ---- Set params (triggers recalc) ----
  const setParams = useCallback((newParams: RadiometricParams) => {
    setParamsState(newParams);
    const ti = thermalImageRef.current;
    if (!ti) return;

    // Recalculate temperature matrix
    ti.temperatures = recalcTemperatures(ti, newParams);
    const n = ti.temperatures.length;
    let min = Infinity, max = -Infinity, sum = 0;
    for (let i = 0; i < n; i++) {
      const v = ti.temperatures[i];
      if (v < min) min = v;
      if (v > max) max = v;
      sum += v;
    }
    ti.minTemp = min;
    ti.maxTemp = max;
    ti.avgTemp = sum / n;

    setStatusMessage(`${formatDims(ti)} · ${ti.cameraModel} · ${ti.minTemp.toFixed(1)}C to ${ti.maxTemp.toFixed(1)}C`);

    // Re-compute all ROI and point stats
    setRois((prev) => {
      const updated = prev.map((r) => computeRoiStats(r));
      notifyChange(updated, points, newParams);
      return updated;
    });
    setPoints((prev) => prev.map((p) => computePointTemp(p)));
  }, [points, computeRoiStats, computePointTemp, notifyChange]);

  // ---- Actions ----
  const undoRoi = useCallback(() => {
    setRois((prev) => {
      const updated = prev.slice(0, -1);
      notifyChange(updated, points, params);
      return updated;
    });
    setActiveRoiIndex(-1);
  }, [points, params, notifyChange]);

  const clearRois = useCallback(() => {
    setRois([]);
    setActiveRoiIndex(-1);
    notifyChange([], points, params);
  }, [points, params, notifyChange]);

  const clearPoints = useCallback(() => {
    setPoints([]);
    setActivePointIndex(-1);
    notifyChange(rois, [], params);
  }, [rois, params, notifyChange]);

  // ---- Pointer handlers ----
  const handlePointerDown = useCallback((imgX: number, imgY: number) => {
    if (!thermalImageRef.current) return;

    if (mode === 'rect') {
      // 1. Hit-test corner handles of existing ROIs
      for (let i = rois.length - 1; i >= 0; i--) {
        const nr = normRoi(rois[i]);
        const corners: Array<{ handle: 'nw' | 'ne' | 'sw' | 'se'; cx: number; cy: number }> = [
          { handle: 'nw', cx: nr.x0, cy: nr.y0 },
          { handle: 'ne', cx: nr.x1, cy: nr.y0 },
          { handle: 'sw', cx: nr.x0, cy: nr.y1 },
          { handle: 'se', cx: nr.x1, cy: nr.y1 },
        ];
        for (const c of corners) {
          if (Math.hypot(imgX - c.cx, imgY - c.cy) < HANDLE_RADIUS) {
            dragRef.current = { type: 'resize', idx: i, handle: c.handle };
            setActiveRoiIndex(i);
            return;
          }
        }
      }

      // 2. Hit-test body of existing ROIs
      for (let i = rois.length - 1; i >= 0; i--) {
        const nr = normRoi(rois[i]);
        if (imgX >= nr.x0 && imgX <= nr.x1 && imgY >= nr.y0 && imgY <= nr.y1) {
          dragRef.current = {
            type: 'move',
            idx: i,
            ox: imgX,
            oy: imgY,
            rx0: rois[i].x0,
            ry0: rois[i].y0,
            rx1: rois[i].x1,
            ry1: rois[i].y1,
          };
          setActiveRoiIndex(i);
          return;
        }
      }

      // 3. Create new ROI
      const newRoi: ThermalRoi = { x0: imgX, y0: imgY, x1: imgX, y1: imgY, stats: null };
      setRois((prev) => {
        const updated = [...prev, newRoi];
        dragRef.current = { type: 'new-rect', idx: updated.length - 1 };
        setActiveRoiIndex(updated.length - 1);
        return updated;
      });
    } else {
      // Point mode
      // 1. Hit-test existing points
      for (let i = points.length - 1; i >= 0; i--) {
        if (Math.hypot(imgX - points[i].x, imgY - points[i].y) < POINT_HIT_RADIUS) {
          dragRef.current = { type: 'move-point', idx: i };
          setActivePointIndex(i);
          return;
        }
      }

      // 2. Create new point
      const newPt: ThermalPoint = { x: imgX, y: imgY, value_c: null };
      setPoints((prev) => {
        const updated = [...prev, newPt];
        dragRef.current = { type: 'new-point', idx: updated.length - 1 };
        setActivePointIndex(updated.length - 1);
        return updated;
      });
    }
  }, [mode, rois, points]);

  const handlePointerMove = useCallback((imgX: number, imgY: number) => {
    const drag = dragRef.current;
    if (!drag) return;

    if (drag.type === 'new-rect') {
      setRois((prev) => {
        const updated = [...prev];
        if (updated[drag.idx]) {
          updated[drag.idx] = { ...updated[drag.idx], x1: imgX, y1: imgY };
        }
        return updated;
      });
    } else if (drag.type === 'resize') {
      setRois((prev) => {
        const updated = [...prev];
        const r = updated[drag.idx];
        if (!r) return prev;
        const copy = { ...r };
        if (drag.handle === 'nw') { copy.x0 = imgX; copy.y0 = imgY; }
        else if (drag.handle === 'ne') { copy.x1 = imgX; copy.y0 = imgY; }
        else if (drag.handle === 'sw') { copy.x0 = imgX; copy.y1 = imgY; }
        else if (drag.handle === 'se') { copy.x1 = imgX; copy.y1 = imgY; }
        updated[drag.idx] = copy;
        return updated;
      });
    } else if (drag.type === 'move') {
      const dx = imgX - drag.ox;
      const dy = imgY - drag.oy;
      setRois((prev) => {
        const updated = [...prev];
        if (updated[drag.idx]) {
          updated[drag.idx] = {
            ...updated[drag.idx],
            x0: drag.rx0 + dx,
            y0: drag.ry0 + dy,
            x1: drag.rx1 + dx,
            y1: drag.ry1 + dy,
          };
        }
        return updated;
      });
    } else if (drag.type === 'new-point' || drag.type === 'move-point') {
      setPoints((prev) => {
        const updated = [...prev];
        if (updated[drag.idx]) {
          updated[drag.idx] = { ...updated[drag.idx], x: imgX, y: imgY, value_c: null };
        }
        return updated;
      });
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;

    if (drag.type === 'new-rect' || drag.type === 'resize' || drag.type === 'move') {
      setRois((prev) => {
        const updated = prev.map((r, i) => i === drag.idx ? computeRoiStats(r) : r);
        setPoints((pts) => {
          notifyChange(updated, pts, params);
          return pts;
        });
        return updated;
      });
    } else if (drag.type === 'new-point' || drag.type === 'move-point') {
      setPoints((prev) => {
        const updated = prev.map((p, i) => i === drag.idx ? computePointTemp(p) : p);
        setRois((rs) => {
          notifyChange(rs, updated, params);
          return rs;
        });
        return updated;
      });
    }
  }, [computeRoiStats, computePointTemp, params, notifyChange]);

  // ---- Serialization ----
  const toJSON = useCallback((): ThermalAnalysisData => ({
    rois,
    points,
    params,
  }), [rois, points, params]);

  const load = useCallback((data: ThermalAnalysisData | undefined) => {
    if (!data) return;
    setRois(data.rois || []);
    setPoints(data.points || []);
    if (data.params) setParamsState(data.params);
  }, []);

  // ---- Snapshot export ----
  const exportSnapshotDataURL = useCallback((
    img: HTMLImageElement,
    drawOverlays: (ctx: CanvasRenderingContext2D) => void,
  ): string | null => {
    if (!img.naturalWidth || !img.naturalHeight) return null;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    try {
      ctx.drawImage(img, 0, 0, w, h);
      drawOverlays(ctx);
      return c.toDataURL('image/png');
    } catch {
      return null;
    }
  }, []);

  return {
    mode, rois, points, activeRoiIndex, activePointIndex,
    isAnalyzing, isAnalyzed, statusMessage, params,
    setMode, analyze, setParams,
    undoRoi, clearRois, clearPoints,
    handlePointerDown, handlePointerMove, handlePointerUp,
    toJSON, load, exportSnapshotDataURL,
  };
}
